import { useContext, useEffect, useState } from "react";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AllStudents from "./AllStudents";
import AdminFacultyDashboard from "./FacultyAddShow/AdminFacultyDashboard";
import AdminApplicationsView from "./Career/AdminApplicationsView";
import AdminStudentDocs from "./StudentTeacherDashboard/AdminStudentDocs";
import AdminTeacherDocs from "./StudentTeacherDashboard/AdminTeacherDocs";
import AdminShortCourses from "./ShortCourses/AdminShortCourses";
import { Toggle } from "lucide-react";

const AdminAllTabs = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all_students");
  const [applicationStatus, setApplicationStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  const { loginUserEmail, setLoginUserEmail, studentInfo, setStudentInfo } =
    useContext(AppContext);

  useEffect(() => {
    fetchApplicationStatus();
    if (loginUserEmail) {
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/students/student-info?email=${loginUserEmail}`
        )
        .then((response) => {
          setStudentInfo(response.data);
        })
        .catch((error) => {
          console.error("Error fetching student info:", error);
        });
    }
  }, [loginUserEmail, setStudentInfo]);

  const fetchApplicationStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/settings/application-status`
      );
      setApplicationStatus(response.data.isOpen);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching application status:", error);
      setLoading(false);
    }
  };

  const toggleApplicationStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/settings/application-status`,
        { isOpen: !applicationStatus }
      );
      setApplicationStatus(response.data.isOpen);
      setLoading(false);
    } catch (error) {
      console.error("Error updating application status:", error);
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar - Fixed */}
      <div className="w-64 p-6 fixed h-full text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 overflow-y-auto">
        <h2 className="text-xl font-bold mb-6">NIIT Admission Dashboard</h2>
        <nav className="space-y-3">
          <button
            className={`flex items-center w-full text-left p-3 rounded ${
              activeTab === "all_students"
                ? "bg-blue-600 text-white"
                : "hover:bg-blue-700 hover:text-white"
            }`}
            onClick={() => setActiveTab("all_students")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
            </svg>
            All Students
          </button>

          {/* Application Status Toggle */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-3 mt-3">
            <div className="flex flex-col space-y-2">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Admission Application Status
              </h3>
              <div className="flex items-center">
                <button
                  onClick={toggleApplicationStatus}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    applicationStatus ? "bg-green-500" : "bg-gray-400"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      applicationStatus ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
                <span className="ml-3 text-sm font-medium">
                  {applicationStatus
                    ? "Applications Open"
                    : "Applications Closed"}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {applicationStatus
                  ? "Apply buttons are currently enabled"
                  : "Apply buttons are currently disabled"}
              </p>
            </div>
          </div>

          {/* Existing buttons */}
          <button
            className={`flex items-center w-full text-left p-3 rounded ${
              activeTab === "staff"
                ? "bg-blue-600 text-white"
                : "hover:bg-blue-700 hover:text-white"
            }`}
            onClick={() => setActiveTab("staff")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
            Staff
          </button>

          {/* Short Courses Button */}
          <button
            className={`flex items-center w-full text-left p-3 rounded ${
              activeTab === "short_courses"
                ? "bg-blue-600 text-white"
                : "hover:bg-blue-700 hover:text-white"
            }`}
            onClick={() => setActiveTab("short_courses")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
            </svg>
            Short Courses
          </button>

          {/* add students Documents */}
          <button
            className={`flex items-center w-full text-left p-3 rounded ${
              activeTab === "students"
                ? "bg-blue-600 text-white"
                : "hover:bg-blue-700 hover:text-white"
            }`}
            onClick={() => setActiveTab("students")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
            </svg>
            Student Documents
          </button>

          {/* Teachers Documents */}
          <button
            className={`flex items-center w-full text-left p-3 rounded ${
              activeTab === "teachers"
                ? "bg-blue-600 text-white"
                : "hover:bg-blue-700 hover:text-white"
            }`}
            onClick={() => setActiveTab("teachers")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
              <path d="M3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zm15.38 0a11.115 11.115 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0 8.968 8.968 0 00-5.35-2.524 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762L9.3 11.038a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 011.517 0.714zM11 5.85l3.763 1.614-3.763 1.614L7.237 7.464 11 5.85z" />
            </svg>
            Teacher Documents
          </button>

          <button
            className={`flex items-center w-full text-left p-3 rounded ${
              activeTab === "careers"
                ? "bg-blue-600 text-white"
                : "hover:bg-blue-700 hover:text-white"
            }`}
            onClick={() => setActiveTab("careers")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                clipRule="evenodd"
              />
              <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
            </svg>
            Career
          </button>
          <button
            className="flex items-center w-full text-left p-3 rounded text-red-500 hover:bg-red-600 hover:text-white"
            onClick={() => {
              localStorage.clear(); // Clears all items in localStorage, or you can use removeItem("key") if you want to remove a specific item
              navigate("/portal");
              setLoginUserEmail(null);
              setStudentInfo(null);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                clipRule="evenodd"
              />
            </svg>
            Logout
          </button>
        </nav>
      </div>

      {/* Main Content - Full-Screen Scroll */}
      <div className="flex-1 ml-64 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-8 bg-white shadow-lg rounded-lg min-h-screen">
          {activeTab === "all_students" && <AllStudents />}
          {activeTab === "staff" && <AdminFacultyDashboard />}
          {activeTab === "careers" && <AdminApplicationsView />}
          {activeTab === "students" && <AdminStudentDocs />}
          {activeTab === "teachers" && <AdminTeacherDocs />}
          {activeTab === "short_courses" && <AdminShortCourses />}
        </div>
      </div>
    </div>
  );
};

export default AdminAllTabs;
